import { TFunction } from 'i18next';
import {
  Package,
  Pencil,
  Trash2,
  Layers,
  Zap,
  AlignLeft,
  Languages,
  Shield,
  Infinity
} from 'lucide-react';
import { DataGridColumn } from '@components/atoms/datatable/data-grid-v2';
import { AdminPlan } from '@utils/types/admin';
import { Elements } from '@utils/providers/theme-context';

type GetColourFn = (element: Elements) => string;

type GetColumnsParams = {
  t: TFunction;
  getColour: GetColourFn;
  isDeleting: boolean;
  onEdit: (plan: AdminPlan) => void;
  onDelete: (plan: AdminPlan) => void;
};

export const getPlanColumns = ({
  t,
  getColour,
  isDeleting,
  onEdit,
  onDelete
}: GetColumnsParams): DataGridColumn<AdminPlan>[] => [
  {
    header: t('sku'),
    accessorKey: 'sku' as keyof AdminPlan,
    minWidth: 180,
    cell: (info: { row: { original: AdminPlan } }) => (
      <div className="flex items-center gap-2">
        <Package
          className="h-4 w-4 flex-shrink-0"
          style={{ color: getColour('inactiveText') }}
        />
        <span
          className="font-medium truncate"
          style={{ color: getColour('normalText') }}
        >
          {info.row.original.sku}
        </span>
        {info.row.original.is_default && (
          <span
            className="px-1.5 py-0.5 text-xs font-medium rounded-2xl flex-shrink-0"
            style={{
              backgroundColor: getColour('thirdLayer'),
              color: getColour('activeText')
            }}
          >
            {t('default')}
          </span>
        )}
      </div>
    )
  },
  {
    header: t('description'),
    accessorKey: 'description' as keyof AdminPlan,
    minWidth: 200,
    cell: (info: { row: { original: AdminPlan } }) => (
      <span
        className="text-sm truncate block"
        style={{ color: getColour('inactiveText'), maxWidth: '250px' }}
        title={info.row.original.description}
      >
        {info.row.original.description}
      </span>
    )
  },
  {
    header: t('type'),
    accessorKey: 'type' as keyof AdminPlan,
    maxWidth: 100,
    cell: (info: { row: { original: AdminPlan } }) => (
      <span
        className="px-2 py-0.5 text-xs font-medium rounded-full capitalize"
        style={{
          backgroundColor: getColour('thirdLayer'),
          color: getColour('normalText')
        }}
      >
        {info.row.original.type.toLowerCase()}
      </span>
    )
  },
  {
    header: t('stt-limit'),
    accessorKey: 'stt_limit' as keyof AdminPlan,
    maxWidth: 140,
    cell: (info: { row: { original: AdminPlan } }) => (
      <span
        className="flex items-center gap-1 text-sm whitespace-nowrap"
        style={{ color: getColour('normalText') }}
      >
        {info.row.original.stt_limit === null ? (
          <>
            <Infinity className="h-3.5 w-3.5" />
            {t('unlimited')}
          </>
        ) : (
          `${info.row.original.stt_limit.toLocaleString()} ${t('min')}`
        )}
      </span>
    )
  },
  {
    header: t('features'),
    id: 'features',
    minWidth: 200,
    cell: (info: { row: { original: AdminPlan } }) => {
      const plan = info.row.original;
      const features = [
        { enabled: plan.batch_enabled, label: t('batch'), icon: Layers },
        { enabled: plan.realtime_enabled, label: t('realtime'), icon: Zap },
        {
          enabled: plan.alignment_enabled,
          label: t('alignment'),
          icon: AlignLeft
        },
        {
          enabled: plan.translation_enabled,
          label: t('translation'),
          icon: Languages
        }
      ];
      const enabledFeatures = features.filter(f => f.enabled);

      if (enabledFeatures.length === 0) {
        return (
          <span
            className="text-xs"
            style={{ color: getColour('inactiveText') }}
          >
            {t('none')}
          </span>
        );
      }

      return (
        <div className="flex flex-wrap gap-1">
          {enabledFeatures.map(({ label, icon: Icon }) => (
            <span
              key={label}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-2xl"
              style={{
                backgroundColor: getColour('thirdLayer'),
                color: getColour('normalText')
              }}
            >
              <Icon className="h-3 w-3" />
              {label}
            </span>
          ))}
        </div>
      );
    }
  },
  {
    header: t('fair-use'),
    accessorKey: 'is_fair_use' as keyof AdminPlan,
    maxWidth: 100,
    cell: (info: { row: { original: AdminPlan } }) => (
      <div className="flex items-center gap-1.5">
        {info.row.original.is_fair_use ? (
          <>
            {}
            <Shield className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400" />
            {}
            <span className="text-xs whitespace-nowrap text-amber-600 dark:text-amber-400">
              {info.row.original.fair_use_rate_limit
                ? `${info.row.original.fair_use_rate_limit}/min`
                : t('enabled')}
            </span>
          </>
        ) : (
          <span
            className="text-xs"
            style={{ color: getColour('inactiveText') }}
          >
            -
          </span>
        )}
      </div>
    )
  },
  {
    header: t('actions'),
    id: 'actions',
    maxWidth: 80,
    cell: (info: { row: { original: AdminPlan } }) => (
      <div className="flex items-center gap-2">
        <button
          onClick={() => onEdit(info.row.original)}
          disabled={info.row.original.is_default}
          className="p-1 rounded-2xl hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
          title={
            info.row.original.is_default
              ? String(t('default-plan-read-only'))
              : String(t('edit-plan'))
          }
        >
          <Pencil
            className="h-4 w-4"
            style={{ color: getColour('inactiveText') }}
          />
        </button>
        <button
          onClick={() => onDelete(info.row.original)}
          disabled={info.row.original.is_default || isDeleting}
          className="p-1 rounded-2xl hover:bg-red-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
          title={
            info.row.original.is_default
              ? String(t('default-plan-cannot-delete'))
              : String(t('delete-plan'))
          }
        >
          {}
          <Trash2 className="h-4 w-4 text-red-500 dark:text-red-400" />
        </button>
      </div>
    )
  }
];
